import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiClient } from '../lib/auth';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FolderItem {
  name: string;
  path: string;
  mediaCount: number;
  totalMediaCount: number;
  createdAt: string;
}

export interface FolderState {
  /** Folders at the currently viewed level */
  folders: FolderItem[];
  /** The current navigation path (array of folder names) */
  currentPath: string[];
  loading: boolean;
  error: string | null;
}

const initialState: FolderState = {
  folders: [],
  currentPath: [],
  loading: false,
  error: null,
};

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------

/**
 * Fetch child folders for a given path from GET /folders.
 * If path is omitted or empty, fetches root-level folders.
 * Requirements: 9.9, 9.10, 9.11, 9.12
 */
export const fetchFolders = createAsyncThunk(
  'folders/fetchFolders',
  async (path: string | undefined, { rejectWithValue }) => {
    try {
      const params: Record<string, string> = {};
      if (path) params.path = path;

      const response = await apiClient.get<{ folders: FolderItem[] }>('/folders', { params });
      return { folders: response.data.folders, path: path ?? '' };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load folders';
      return rejectWithValue(message);
    }
  },
);

/**
 * Create a new folder via POST /folders.
 * Requirements: 9.3, 9.4
 */
export const createFolder = createAsyncThunk(
  'folders/createFolder',
  async ({ parentPath, name }: { parentPath: string[]; name: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post<{
        folderPath: string[];
        name: string;
        parentPath: string;
        createdAt: string;
        createdBy: string;
      }>('/folders', { parentPath, name });
      return response.data;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create folder';
      return rejectWithValue(message);
    }
  },
);

/**
 * Rename/move a folder via PUT /folders.
 * Requirements: 9.7, 9.8
 */
export const renameFolder = createAsyncThunk(
  'folders/renameFolder',
  async (
    { currentPath, newPath }: { currentPath: string[]; newPath: string[] },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiClient.put<{ updated: boolean; mediaFilesUpdated: number }>(
        '/folders',
        { currentPath, newPath },
      );
      return response.data;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to rename folder';
      return rejectWithValue(message);
    }
  },
);

/**
 * Delete an empty folder via DELETE /folders.
 * Requirements: 9.5, 9.6
 */
export const deleteFolder = createAsyncThunk(
  'folders/deleteFolder',
  async (path: string[], { rejectWithValue }) => {
    try {
      const response = await apiClient.delete<{ deleted: boolean }>('/folders', {
        data: { path },
      });
      return response.data;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete folder';
      return rejectWithValue(message);
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const folderSlice = createSlice({
  name: 'folders',
  initialState,
  reducers: {
    setCurrentPath(state, action: PayloadAction<string[]>) {
      state.currentPath = action.payload;
    },
    clearFolders(state) {
      state.folders = [];
      state.currentPath = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchFolders
    builder
      .addCase(fetchFolders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFolders.fulfilled, (state, action) => {
        state.folders = action.payload.folders;
        state.currentPath = action.payload.path
          ? action.payload.path.split('/')
          : [];
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchFolders.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Failed to load folders';
      });

    // createFolder
    builder
      .addCase(createFolder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createFolder.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(createFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Failed to create folder';
      });

    // renameFolder
    builder
      .addCase(renameFolder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(renameFolder.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(renameFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Failed to rename folder';
      });

    // deleteFolder
    builder
      .addCase(deleteFolder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFolder.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Failed to delete folder';
      });
  },
});

export const { setCurrentPath, clearFolders } = folderSlice.actions;

// Selectors
export const selectFolderItems = (state: { folders: FolderState }) => state.folders.folders;
export const selectFolderCurrentPath = (state: { folders: FolderState }) =>
  state.folders.currentPath;
export const selectFolderLoading = (state: { folders: FolderState }) => state.folders.loading;
export const selectFolderError = (state: { folders: FolderState }) => state.folders.error;

export default folderSlice.reducer;
